import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';

export interface RetryOptions {
  /** Total attempts (including the first). Default: 3. */
  maxAttempts?: number;
  /** Base delay in ms. Default: 300. */
  baseDelayMs?: number;
  /** Cap on the computed delay. Default: 8000. */
  maxDelayMs?: number;
  /** Custom predicate — returns `true` to retry. Default: transient errors only. */
  shouldRetry?: (error: FetchBaseQueryError, attempt: number, args: string | FetchArgs) => boolean;
}

/**
 * Wraps any `BaseQueryFn` in an exponential-backoff retry policy.
 *
 * **Only** retries safe (idempotent) requests by default: GET / HEAD / PUT /
 * DELETE. Retries on:
 * - Network-level failures (`FETCH_ERROR`, `TIMEOUT_ERROR`)
 * - 408 Request Timeout
 * - 429 Too Many Requests (respects `Retry-After` if present)
 * - 5xx Server Error
 *
 * Exponential backoff with full jitter:
 *   `sleep = random(0, min(maxDelay, base * 2^attempt))`
 */
export function withRetry(
  inner: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  options: RetryOptions = {},
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  const { maxAttempts = 3, baseDelayMs = 300, maxDelayMs = 8_000 } = options;
  const predicate = options.shouldRetry ?? defaultShouldRetry;

  return async (args, api, extra) => {
    let lastResult = await inner(args, api, extra);
    let attempt = 1;

    while (
      lastResult.error &&
      attempt < maxAttempts &&
      predicate(lastResult.error, attempt, args)
    ) {
      const delay = computeDelay(lastResult.error, attempt, baseDelayMs, maxDelayMs);
      const aborted = await sleep(delay, api);
      if (aborted) return lastResult;
      lastResult = await inner(args, api, extra);
      attempt += 1;
    }

    return lastResult;
  };
}

function defaultShouldRetry(
  error: FetchBaseQueryError,
  _attempt: number,
  args: string | FetchArgs,
): boolean {
  if (!isIdempotent(args)) return false;
  if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') return true;
  if (typeof error.status === 'number') {
    return error.status === 408 || error.status === 429 || error.status >= 500;
  }
  return false;
}

function isIdempotent(args: string | FetchArgs): boolean {
  if (typeof args === 'string') return true;
  const method = (args.method ?? 'GET').toUpperCase();
  return method === 'GET' || method === 'HEAD' || method === 'PUT' || method === 'DELETE';
}

function computeDelay(
  error: FetchBaseQueryError,
  attempt: number,
  base: number,
  max: number,
): number {
  // Honor Retry-After on 429 responses when available.
  if (error.status === 429 && typeof error === 'object') {
    const headers = (error as { meta?: { response?: { headers?: Headers } } }).meta?.response
      ?.headers;
    const retryAfter = headers?.get('Retry-After');
    if (retryAfter) {
      const asSeconds = Number.parseFloat(retryAfter);
      if (!Number.isNaN(asSeconds) && asSeconds > 0) return Math.min(asSeconds * 1000, max);
    }
  }
  const capped = Math.min(max, base * 2 ** (attempt - 1));
  // Full jitter — see https://www.awsarchitectureblog.com/2015/03/backoff.html
  return Math.floor(Math.random() * capped);
}

function sleep(ms: number, api: BaseQueryApi): Promise<boolean> {
  return new Promise((resolve) => {
    const handle = window.setTimeout(() => {
      resolve(false);
    }, ms);
    api.signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(handle);
        resolve(true);
      },
      { once: true },
    );
  });
}
